import cn from "classnames";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    _no,
    _notEmpty,
    computeUSDAmount,
    formatDecimals,
    formatMoney,
    formatMoneyDashed,
} from "../../../../utils";

import { useSelector } from "react-redux";
import { formatUnits } from "viem";
import ConnectWalletModal from "../../../../components/ConnectWalletModal";
import Modal from "../../../../components/Modal";
// import Tooltip from "../../../../components/Tooltip";
import useConfig from "../../../../customHooks/useConfig";
import useRWAPositionNFT from "../../../../customHooks/useRWAPositionNFT";
import useTokenBalance from "../../../../customHooks/useTokenBalance";
import { ALL_SUPPORTED_POOLS, getSupportedTokens } from "../../../../ethers/tokens";
import { getMaxRwaVaultAmountUsd } from "../../../../utils/config";
import { FarmingContext, useFarmingContext } from "../../context";
import OpenRwaFarmDetails from "../OpenRwaFarmDetails";
import ProductLogo from "../Row/ProductLogo";
import styles from "./Row.module.sass";
import ViewRWAFarmDetails from "./ViewRWAFarmDetails";
import { getContractByField } from "../../../../ethers/contracts";
// import { FaFileExport } from "react-icons/fa6";
import WithdrawModal from "./WithdrawQueue";

const Row = ({
    item,
}) => {
    const [visibleConnect, setVisibleConnect] = useState(false);
    const [modalType, setModalType] = useState();
    const [lastOpened, setLastOpened] = useState();
    const farmingContext = useFarmingContext();

    const farmContext = useContext(FarmingContext);

    const [refetch, setRefetch] = useState(false);

    const config = useConfig();
    const {
        chainId,
    } = config;

    const vaultCapacity = useMemo(() => {
        return getMaxRwaVaultAmountUsd(chainId);
    }, [chainId]);

    const forwardingDepositContract = useMemo(() => {
            const contract = getContractByField(chainId, item.contractField);
            console.log('Row: Contract lookup', {
                chainId,
                contractField: item.contractField,
                contract: contract ? { hasAbi: !!contract.abi, hasAddress: !!contract.address } : null
            });
            // Return null if contract doesn't have required fields
            if (!contract || !contract.abi || !contract.address) {
                return null;
            }
            return contract;
        },
        [chainId, item.contractField]
    );
    
    const supportedTokens = useMemo(
        () => {
            return getSupportedTokens(
                chainId,
                ALL_SUPPORTED_POOLS
            );
        },
        [chainId]
    );

    const token = supportedTokens.find(
        (token) => {
            return token.name === item.defaultToken
                && token?.isAavePool === (item.tokens?.[item.defaultToken]?.isAave ?? false)
                && token?.isRWA === true;
        }
    );

    const {
        address,
        nftId
    } = useSelector(
        (state) => state.settings
    );

    const {
        pricingFeeds,
        walletBalance,
    } = useSelector(
        (state) => state.token
    );

    const tokenPriceData = pricingFeeds.find(
        item => item.name === token.name
    );

    const tokenPrice = tokenPriceData?.price ?? 0;

    const { tokenBalance } = useTokenBalance({
        config,
        nftId,
        token,
        userAccount: address,
        price: tokenPrice,
        walletBalance
    });

    const handleCloseModal = () => setModalType(null);
    const handleOpenModal = (modal) => {
        setLastOpened(
            modal === "connect"
                ? lastOpened
                : modal
        );

        if (modal === "connect") {
            setVisibleConnect(true);
            return;
        }

        setModalType(modal);
    };

    const rwaPosition = useRWAPositionNFT({
        chainId,
        forwardingDepositContract: forwardingDepositContract,
        rwaDefaultToken: item.defaultToken,
        userAddress: address,
        refetch
    });

    const formattedPositionAmount = useMemo(() => 
        formatUnits(
            rwaPosition?.positionData ?? 0,
            token.decimals
        ), 
        [rwaPosition?.positionData, token.decimals]
    );


    const formattedPendingRewardsAmount = formatUnits(
        rwaPosition?.rewardsData ?? 0,
        token.decimals
    )
        
    const totalDeposited = formatUnits(
        rwaPosition?.totalDepositedData ?? 0,
        token.decimals
    );

    const positionAmountInUSD = useMemo(() => {
        return computeUSDAmount(
            token.decimals,
            formattedPositionAmount.toString(),
            tokenPrice
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        rwaPosition,
        formattedPositionAmount
    ]);

    const rewardsInUSD = useMemo(() => {
        return computeUSDAmount(
            token.decimals,
            formattedPendingRewardsAmount.toString(),
            tokenPrice
        );
    }, [formattedPendingRewardsAmount, token.decimals, tokenPrice]);

    useEffect(() => {
      setRefetch(!refetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainId])
    

    useEffect(() => {
        if (farmingContext) {
            farmingContext.addUpdateActiveRWA({
                id: item.id,
                rewardsValue:
                    item.active
                        ? Number(positionAmountInUSD) + Number(rewardsInUSD)
                        : 0,
                positionAmountInUSD: Number(positionAmountInUSD),
                isLoading: rwaPosition?.isFetching,
                poolName: token.poolName,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        positionAmountInUSD,
        rewardsInUSD,
    ]);

    useEffect(() => {
        if (item.APY) {
            farmContext.addOpenFarmAPY(
                token.poolName,
                item.APY
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.APY]);

    const data = useMemo(() => ({
        ...item,
        totalDeposited,
        capacity: vaultCapacity - totalDeposited,
        details: [
            {
                title: "Position Amount",
                content: `${formatMoney(formattedPositionAmount)} ${token.name}`,
            },
            modalType !== "withdraw-position" && {
                title: "Fixed APY",
                content: `20%`,
            },
            modalType !== "withdraw-position" && {
                title: "Available Interest",
                content: `${formatMoney(formattedPendingRewardsAmount)} ${token.name}`,
            },
        ].filter(Boolean),
    }), [item, totalDeposited, vaultCapacity, formattedPositionAmount, token.name, modalType, formattedPendingRewardsAmount]);

    return (
        <>
            <div
                className={cn(
                    styles.row,
                )}
            >
                <div className={styles.col} style={{ display: "none" }}></div>
                <div className={styles.col}>
                    <div
                        className={styles.productItem}
                    >
                        <ProductLogo item={item} />

                        <div className={styles.details}>
                            <div>
                                <div className={styles.product}>{item.source}</div>
                                <div className={styles.link}>{item.description}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.col}>
                    <div className={styles.item}>
                        <div className={styles.label}>Filled Capacity</div>
                        <div className={styles.value}>
                            {formatMoney(totalDeposited)} / {formatMoney(vaultCapacity)} {token.name}
                        </div>
                    </div>
                </div>
                <div className={styles.col}>
                    <div className={styles.item}>
                        <div className={styles.label}>APY</div>
                        <div className={styles.apyValue}>
                            <div className="status-purple-dark"
                            >
                                {
                                    `${formatDecimals(
                                        item.APY
                                    )}%`
                                }
                            </div>
                        </div>
                    </div>
                </div>
                {/* <div className={styles.col}>
                    <div className={styles.item}>
                        <div className={styles.label}>Reward Cycle</div>
                        <div className={styles.value}>
                            30 Days
                            // <Tooltip
                            //     className={cn(
                            //         styles.tooltip,
                            //     )}
                            //     title={"tooltip text"}
                            //     icon="info-stroke"
                            //     place="right"
                            // />
                        </div>
                    </div>
                </div> */}
                <div className={styles.col}>
                    <div className={styles.item}>
                        <div className={styles.label}>Your Position</div>
                        <div className={styles.value}>
                            {formatMoneyDashed(formattedPositionAmount)}
                        </div>
                    </div>
                </div>
                <div className={styles.col}>
                    <div className={styles.buttons}>
                        {_no(rwaPosition.isFetching) ?
                            <>
                                {!!Number(formattedPositionAmount) ? (
                                    <>
                                        <button
                                            className={cn(
                                                "button",
                                                styles.button,
                                                styles.supply,
                                                styles.viewButton,
                                                {
                                                    // disabled: isDeprecated,
                                                    // [styles.disabled]: isDeprecated
                                                    // disabled: true,
                                                    // [styles.disabled]: true
                                                }
                                            )}
                                            onClick={() => handleOpenModal("view-position")}
                                        >
                                            View Position
                                        </button>
                                        {/* <button
                                            className={cn(
                                                "button",
                                                styles.button,
                                                styles.supply,
                                                styles.withdrawButton,
                                                {
                                                    // disabled: isDeprecated,
                                                    // [styles.disabled]: isDeprecated
                                                    // disabled: true,
                                                    // [styles.disabled]: true
                                                }
                                            )}
                                            onClick={() => handleOpenModal("withdraw-position")}
                                        >
                                           <FaFileExport/>
                                        </button> */}
                                    </>
                                ) : (
                                    <button
                                        className={cn(
                                            "button",
                                            styles.button,
                                            styles.supply,
                                            {
                                                // disabled: isDeprecated,
                                                // [styles.disabled]: isDeprecated
                                                // disabled: true,
                                                // [styles.disabled]: true
                                            }
                                        )}
                                        onClick={() => handleOpenModal("open-position")}
                                    >
                                        Open Position
                                    </button>
                                )}
                            </> : (
                                <button className={cn(
                                    "button",
                                    styles.button,
                                    {
                                        disabled: true,
                                        [styles.disabled]: true
                                    },
                                )}>
                                    Loading...
                                </button>
                            )
                        }
                    </div>
                </div>
            </div>
            <Modal
                outerClassName={cn({
                    [styles.openOuter]: modalType === "open-position",
                    [styles.viewOuter]: modalType === "view-position"
                })}
                visible={_notEmpty(modalType)}
                onClose={handleCloseModal}
            >
                {
                    modalType === "open-position" && (
                        <OpenRwaFarmDetails
                            item={data}
                            onClose={handleCloseModal}
                            token={tokenBalance}
                            onConnect={() => handleOpenModal("connect")}
                            onDone={handleCloseModal}
                            doRefetch={() => setRefetch(!refetch)}
                        />
                    )
                }
                {
                    modalType === "view-position" && (
                        <ViewRWAFarmDetails
                            item={data}
                            token={tokenBalance}
                            onClose={handleCloseModal}
                            onConnect={() => handleOpenModal("connect")}
                            onDone={handleCloseModal}
                            pendingRewards={formattedPendingRewardsAmount}
                            positionAmount={formattedPositionAmount}
                            doRefetch={() => setRefetch(!refetch)}
                        />
                    )
                }
                {
                    modalType === "withdraw-position" && (
                        <WithdrawModal
                            item={data}
                            token={tokenBalance}
                            onClose={handleCloseModal}
                            onConnect={() => handleOpenModal("connect")}
                            onDone={handleCloseModal}
                            pendingRewards={formattedPendingRewardsAmount}
                            positionAmount={formattedPositionAmount}
                            doRefetch={() => setRefetch(!refetch)}
                        />
                    )
                }
            </Modal>
            <ConnectWalletModal
                visible={visibleConnect}
                onClose={() => handleOpenModal(lastOpened)}
            />
        </>
    );
};

export default Row;
